/**
 * Socket → Game engine 이벤트 어댑터 — Plan 08-02 Task 2 (GAME-01).
 *
 * Wave 0 `donation.completed.v1` WS 이벤트 (schema: widgetDonationToastSchema) 를
 * 수신하여 engine.dispatchDonation(DonationEvent) 로 매핑/전파.
 *
 * 설계 의도:
 *   - game-events 는 socket / engine 양쪽에 decouple — 테스트에서 Socket stub 주입 용이.
 *   - Zod 재검증 (T-08-02-01 Spoofing) — schema 불일치 drop + warn.
 *   - unwire() 반환 — HMR/destroy 시 리스너 해제.
 */
import { widgetDonationToastSchema } from '@loop/contracts/ws';
import type { GameEngine } from './engine.js';
import type { DonationEvent } from './game-interface.js';

/** Socket.IO-like 최소 subset — 테스트 stub 이 흉내낼 수 있도록. */
export interface SocketLike {
  on(event: string, listener: (raw: unknown) => void): unknown;
  off(event: string, listener: (raw: unknown) => void): unknown;
}

/** `donation.completed.v1` 이름을 상수로 고정 — socket/client.ts 와 일치. */
export const DONATION_WS_EVENT = 'donation.completed.v1' as const;

/**
 * Socket payload → DonationEvent 매핑.
 *
 * Wave 0 schema 에는 streamerId/fanId/receivedAt 이 포함되지 않음 → undefined 필드 허용.
 * 엔진은 eventId (= donationId uuid) 만 있으면 dedupe 가능.
 */
export function toDonationEvent(payload: {
  donationId: string;
  amountKrw: number;
  nickname: string;
  message: string | null;
}): DonationEvent {
  return {
    eventId: payload.donationId,
    amount: payload.amountKrw,
    nickname: payload.nickname,
    message: payload.message ?? '',
    at: new Date().toISOString(),
  };
}

/**
 * WS `donation.completed.v1` 를 engine.dispatchDonation 으로 wire.
 * Return value: unwire 함수 — HMR / destroy 시 호출.
 */
export function wireDonationHandler(socket: SocketLike, engine: GameEngine): () => void {
  const listener = (raw: unknown): void => {
    const parsed = widgetDonationToastSchema.safeParse(raw);
    if (!parsed.success) {
      console.warn('[game-events] donation payload rejected — schema mismatch', {
        issues: parsed.error?.issues,
      });
      return;
    }
    const event = toDonationEvent(parsed.data);
    void engine.dispatchDonation(event);
  };
  socket.on(DONATION_WS_EVENT, listener);
  return () => {
    socket.off(DONATION_WS_EVENT, listener);
  };
}
